import { Text, Toaster } from '@bsf/force-ui';
import apiFetch from '@wordpress/api-fetch';
import { useEffect, useMemo, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import Header from './components/Header';
import ActionItems from './components/ActionItems';
import VideoModal from './components/VideoModal';
import useStore, { actionTypes } from './store';
import '@/style.scss';
import LearnMoreDialog from './components/LearnMoreDialog';
import ConnectDomain from '@/components/ConnectDomain';

const App = () => {
	const { dispatch, actionItems = [], actionCategories = {} } = useStore();
	const [ isLoading, setIsLoading ] = useState( false );

	useEffect( () => {
		const fetchActionItemsData = async () => {
			const response = await apiFetch( {
				path: 'getting-started/v1/action-items',
				method: 'POST',
				headers: {
					'X-WP-Nonce': gettingStartedVars.rest_api_nonce,
				},
			} );

			if ( response.success && response?.data ) {
				return response.data;
			}

			return [];
		};

		setIsLoading( true );
		fetchActionItemsData()
			.then( ( data ) => {
				dispatch( {
					type: actionTypes.SET_ACTION_ITEMS,
					payload: data?.items || [],
				} );
				dispatch( {
					type: actionTypes.SET_ACTION_CATEGORIES,
					payload: data?.categories || {},
				} );
				setIsLoading( false );
			} )
			.catch( console.error )
			.finally( () => setIsLoading( false ) );
	}, [] );

	// Remove source parameter from URL
	useEffect( () => {
		const url = new URL( window.location );
		url.searchParams.delete( 'source' );
		window.history.replaceState( {}, '', url );
	}, [] );

	const categorizedActionItems = useMemo( () => {
		const categorized = { skeleton: [] }; // Default for skeleton preview.
		actionItems.forEach( ( item ) => {
			const category = item?.category || 'uncategorized';
			if ( ! categorized[ category ] ) {
				categorized[ category ] = [];
			}
			categorized[ category ].push( item );
		} );

		return categorized;
	}, [ actionItems ] );

	const firstIncompleteActionItemId = useMemo( () => {
		return actionItems?.find(
			( item ) =>
				item?.steps?.length !==
				item?.steps?.filter( ( step ) => step?.completed ).length
		)?.id;
	}, [ actionItems ] );

	return (
		<div className="bg-background-secondary min-h-[calc(100vh-46px)] md:min-h-[calc(100vh-32px)] font-figtree">
			<Header />

			<div className="flex flex-col gap-6 sm:gap-8 py-6 sm:py-8 md:py-10 px-4 sm:px-6 lg:px-0 m-auto max-w-[768px]">
				<div className="flex flex-col gap-2">
					<Text
						as="h3"
						size={ 20 }
						className="sm:text-2xl"
						weight={ 600 }
					>
						{ __(
							'Finish Setting Up Your Website',
							'getting-started'
						) }
					</Text>
					<Text size={ 14 }>
						{ __(
							'Let’s get your new website fully ready. Follow this quick checklist to customize, configure, and launch with confidence.',
							'getting-started'
						) }
					</Text>
				</div>

				{ gettingStartedVars?.importTemplateType === 'ai' && (
					<ConnectDomain />
				) }
				{ categorizedActionItems &&
					Object.entries( categorizedActionItems )?.map(
						( [ category, items ] ) => (
							<ActionItems
								key={ category }
								category={ actionCategories[ category ] || {} }
								items={ items }
								defaultValue={ firstIncompleteActionItemId }
								isLoading={ isLoading }
							/>
						)
					) }
			</div>

			<VideoModal />
			<LearnMoreDialog />

			<Toaster
				position="top-right"
				design="stack"
				theme="light"
				autoDismiss={ true }
				dismissAfter={ 5000 }
			/>
		</div>
	);
};

export default App;
